import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import { TextAreaField, TextField, ToggleSwitch } from "@/components/base/FormControls";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import type { Branch } from "@/types";

interface BranchFormModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  branch: Branch | null;
}

interface BranchForm {
  name: string;
  code: string;
  address: string;
  phone: string;
  rnc: string;
  email: string;
  schedule: string;
  whatsapp: string;
  facebook: string;
  instagram: string;
  tiktok: string;
  youtube: string;
  invoice_prefix: string;
  invoice_sequence: string;
  active: boolean;
}

const EMPTY: BranchForm = {
  name: "",
  code: "",
  address: "",
  phone: "",
  rnc: "",
  email: "",
  schedule: "",
  whatsapp: "",
  facebook: "",
  instagram: "",
  tiktok: "",
  youtube: "",
  invoice_prefix: "RKC",
  invoice_sequence: "1",
  active: true,
};

export default function BranchFormModal({ open, onClose, onSaved, branch }: BranchFormModalProps) {
  const toast = useToast();
  const { profile, roleName } = useAuth();
  const [form, setForm] = useState<BranchForm>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (branch) {
      setForm({
        name: branch.name,
        code: branch.code ?? "",
        address: branch.address ?? "",
        phone: branch.phone ?? "",
        rnc: branch.rnc ?? "",
        email: branch.email ?? "",
        schedule: branch.schedule ?? "",
        whatsapp: branch.whatsapp ?? "",
        facebook: branch.facebook ?? "",
        instagram: branch.instagram ?? "",
        tiktok: branch.tiktok ?? "",
        youtube: branch.youtube ?? "",
        invoice_prefix: branch.invoice_prefix ?? "RKC",
        invoice_sequence: String(branch.invoice_sequence ?? 1),
        active: branch.active,
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, branch]);

  const update = <K extends keyof BranchForm>(key: K, value: BranchForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!form.name.trim()) {
      setError("El nombre de la sucursal es obligatorio.");
      return;
    }

    const payload = {
      name: form.name.trim(),
      code: form.code.trim() || null,
      address: form.address.trim() || null,
      phone: form.phone.trim() || null,
      rnc: form.rnc.trim() || null,
      email: form.email.trim() || null,
      schedule: form.schedule.trim() || null,
      whatsapp: form.whatsapp.trim() || null,
      facebook: form.facebook.trim() || null,
      instagram: form.instagram.trim() || null,
      tiktok: form.tiktok.trim() || null,
      youtube: form.youtube.trim() || null,
      invoice_prefix: form.invoice_prefix.trim() || null,
      invoice_sequence: Number.parseInt(form.invoice_sequence, 10) || 1,
      active: form.active,
    };

    setSubmitting(true);
    try {
      if (branch) {
        const { error: updateError } = await supabase
          .from("branches")
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq("id", branch.id);
        if (updateError) {
          setError("No se pudo actualizar la sucursal.");
          return;
        }
        await logAudit({
          userId: profile?.id ?? null,
          userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
          action: "editar",
          entity: "branches",
          entityId: branch.id,
          branchId: branch.id,
          oldValue: { name: branch.name, code: branch.code, active: branch.active },
          newValue: payload,
        });
        toast.success("Sucursal actualizada correctamente.");
      } else {
        const { data, error: insertError } = await supabase
          .from("branches")
          .insert(payload)
          .select("id")
          .single();
        if (insertError || !data) {
          setError("No se pudo crear la sucursal.");
          return;
        }
        await logAudit({
          userId: profile?.id ?? null,
          userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
          action: "crear",
          entity: "branches",
          entityId: data.id,
          branchId: data.id,
          newValue: payload,
        });
        toast.success("Sucursal creada correctamente.");
      }
      onSaved();
      onClose();
    } catch {
      setError("Ocurrió un error inesperado. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={branch ? "Editar sucursal" : "Nueva sucursal"}
      subtitle="Cada sucursal tiene su propia dirección, datos fiscales y numeración de facturas."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="branch-form"
            disabled={submitting}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
          >
            {submitting ? <i className="ri-loader-4-line animate-spin text-base"></i> : null}
            {branch ? "Guardar cambios" : "Crear sucursal"}
          </button>
        </>
      }
    >
      <form id="branch-form" onSubmit={handleSubmit} className="flex flex-col gap-6">
        <section className="flex flex-col gap-4">
          <h3 className="font-heading text-sm font-semibold text-foreground-900">
            Información general
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="Nombre de la sucursal"
              name="name"
              value={form.name}
              onChange={(value) => update("name", value)}
              placeholder="Sucursal Principal"
              required
            />
            <TextField
              label="Código"
              name="code"
              value={form.code}
              onChange={(value) => update("code", value.toUpperCase())}
              placeholder="MATRIZ"
              hint="Identificador corto, por ejemplo MATRIZ o SUC-01."
            />
          </div>
          <TextAreaField
            label="Dirección"
            name="address"
            value={form.address}
            onChange={(value) => update("address", value)}
            placeholder="Av. Principal #123, Ciudad"
            rows={2}
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="Teléfono"
              name="phone"
              value={form.phone}
              onChange={(value) => update("phone", value)}
              placeholder="809-000-0000"
              inputMode="tel"
            />
            <TextField
              label="RNC"
              name="rnc"
              value={form.rnc}
              onChange={(value) => update("rnc", value)}
              placeholder="1-01-00000-1"
            />
            <TextField
              label="Correo"
              name="email"
              type="email"
              value={form.email}
              onChange={(value) => update("email", value)}
              placeholder="sucursal@rkc.com"
              inputMode="email"
            />
            <TextField
              label="Horario"
              name="schedule"
              value={form.schedule}
              onChange={(value) => update("schedule", value)}
              placeholder="Lun a Sáb, 8:00 a.m. - 6:00 p.m."
            />
          </div>
        </section>

        <section className="flex flex-col gap-4">
          <h3 className="font-heading text-sm font-semibold text-foreground-900">
            Contacto y redes sociales
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="WhatsApp"
              name="whatsapp"
              value={form.whatsapp}
              onChange={(value) => update("whatsapp", value)}
              placeholder="809-000-0000"
              inputMode="tel"
            />
            <TextField
              label="Facebook"
              name="facebook"
              value={form.facebook}
              onChange={(value) => update("facebook", value)}
              placeholder="facebook.com/rkcmotor"
            />
            <TextField
              label="Instagram"
              name="instagram"
              value={form.instagram}
              onChange={(value) => update("instagram", value)}
              placeholder="instagram.com/rkcmotor"
            />
            <TextField
              label="TikTok"
              name="tiktok"
              value={form.tiktok}
              onChange={(value) => update("tiktok", value)}
              placeholder="tiktok.com/@rkcmotor"
            />
            <TextField
              label="YouTube"
              name="youtube"
              value={form.youtube}
              onChange={(value) => update("youtube", value)}
              placeholder="youtube.com/@rkcmotor"
            />
          </div>
        </section>

        <section className="flex flex-col gap-4">
          <h3 className="font-heading text-sm font-semibold text-foreground-900">
            Facturación y estado
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="Prefijo de factura"
              name="invoice_prefix"
              value={form.invoice_prefix}
              onChange={(value) => update("invoice_prefix", value.toUpperCase())}
              placeholder="RKC"
              hint="Se usa para la numeración de las facturas de esta sucursal."
            />
            <TextField
              label="Siguiente número"
              name="invoice_sequence"
              value={form.invoice_sequence}
              onChange={(value) => update("invoice_sequence", value.replace(/[^0-9]/g, ""))}
              placeholder="1"
              inputMode="numeric"
            />
          </div>
          <ToggleSwitch
            label="Sucursal activa"
            description="Las sucursales inactivas no podrán operar el POS ni registrar ventas."
            checked={form.active}
            onChange={(checked) => update("active", checked)}
          />
        </section>

        {error ? (
          <div className="animate-shake flex items-start gap-2.5 rounded-md border border-primary-300 bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
              <i className="ri-error-warning-line text-base"></i>
            </span>
            <span className="flex-1 leading-snug">{error}</span>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}